import { jsPDF } from "jspdf";

/**
 * Generates and downloads the official Political Clearance Form PDF for IVEC 2027 delegates.
 * Designed to match the exact specimen layout from the conference authority.
 *
 * @param {Object} participant - Participant information object
 * @param {boolean} [shouldDownload=true] - Whether to automatically trigger browser download
 * @returns {jsPDF} The jsPDF document instance
 */
export const generateParticipantPdf = (participant, shouldDownload = true) => {
    if (!participant) return null;

    const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
    });

    const formatDate = (dt) => {
        if (!dt) return "—";
        try {
            const d = new Date(dt);
            if (isNaN(d.getTime())) return String(dt);
            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            return `${day}-${month}-${year}`;
        } catch {
            return String(dt);
        }
    };

    const formatDateTime = (dt) => {
        if (!dt) return "—";
        try {
            const d = new Date(dt);
            if (isNaN(d.getTime())) return String(dt);
            return d.toLocaleString("en-IN", {
                dateStyle: "medium",
                timeStyle: "short",
            });
        } catch {
            return String(dt);
        }
    };

    const fullName = participant.fullName || participant.name || "—";
    const designation = participant.designation || participant.role || "—";
    const organization = participant.organization || participant.institution || "—";
    const nationality = participant.nationality || "—";
    const passportNumber = participant.passportNumber || participant.passportNo || "—";
    const dateOfIssue = formatDate(participant.dateOfIssue);
    const dateOfExpiry = formatDate(participant.dateOfExpiry);
    const placeOfIssue = participant.placeOfIssue || "—";
    const countryOfBirth = participant.countryOfBirth || "—";
    const emailId = participant.emailId || participant.email || "—";
    const mobileNumber = participant.mobileNumber || participant.mobile || "—";
    const eventDetails =
        participant.eventDetails && participant.eventDetails.trim()
            ? participant.eventDetails.trim()
            : "International Vacuum Electronics Conference (IVEC’27)";

    const refId = participant.id
        ? participant.id.substring(0, Math.min(8, participant.id.length)).toUpperCase()
        : "PORTAL";
    const submissionDate = formatDateTime(participant.createdDate || new Date());

    // -------------------------------------------------------------
    // 1. MAIN DOCUMENT TITLE (Exact Specimen)
    // -------------------------------------------------------------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(
        "Participant Information for Attending IVEC-2027 for",
        105,
        22,
        { align: "center" }
    );
    doc.text(
        "Political Clearance for attending the conference",
        105,
        29,
        { align: "center" }
    );

    // -------------------------------------------------------------
    // 2. TABLE RENDERING (3 Columns matching specimen)
    // Table bounds: x = 14 to 196 (Total width = 182 mm)
    // Col 1: S.No (12 mm) | Col 2: Field (78 mm) | Col 3: Value (92 mm)
    // -------------------------------------------------------------
    const startX = 14;
    const colW1 = 12;
    const colW2 = 78;
    const colW3 = 92;
    const tableWidth = colW1 + colW2 + colW3; // 182 mm
    let currentY = 38;

    const drawGridRow = (sNo, fieldTitle, fieldSubtitle, valueText, isSectionHeader = false) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        // Text wrapping for value
        const splitValue = doc.splitTextToSize(valueText || "", colW3 - 6);
        const valueHeight = splitValue.length * 4.4;

        // Text height for field title & subtitle
        let fieldHeight = 5;
        if (fieldSubtitle) {
            const splitSub = doc.splitTextToSize(fieldSubtitle, colW2 - 6);
            fieldHeight += splitSub.length * 3.6 + 1;
        }

        const rowHeight = Math.max(7.8, valueHeight + 3.8, fieldHeight + 3.5);

        // Background for section headers
        if (isSectionHeader) {
            doc.setFillColor(255, 255, 255);
            doc.rect(startX, currentY, tableWidth, rowHeight, "F");
        }

        // Draw Row Border
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.25);
        doc.rect(startX, currentY, tableWidth, rowHeight);

        // Column Dividers
        doc.line(startX + colW1, currentY, startX + colW1, currentY + rowHeight);
        doc.line(startX + colW1 + colW2, currentY, startX + colW1 + colW2, currentY + rowHeight);

        // 1. S.No Cell
        if (sNo) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(0, 0, 0);
            doc.text(sNo, startX + colW1 / 2, currentY + 5.2, { align: "center" });
        }

        // 2. Field Label Cell
        doc.setFont("helvetica", isSectionHeader ? "bold" : (sNo ? "bold" : "normal"));
        doc.setFontSize(isSectionHeader ? 9.5 : (sNo ? 9.5 : 9));
        doc.setTextColor(0, 0, 0);
        doc.text(fieldTitle, startX + colW1 + 3, currentY + 5.2);

        if (fieldSubtitle) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(70, 70, 70);
            const splitSub = doc.splitTextToSize(fieldSubtitle, colW2 - 6);
            doc.text(splitSub, startX + colW1 + 3, currentY + 9.5);
        }

        // 3. Value Cell
        if (valueText) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(0, 0, 0);
            doc.text(splitValue, startX + colW1 + colW2 + 3, currentY + 5.2);
        }

        currentY += rowHeight;
    };

    // Row 1: Full Name
    drawGridRow("1.", "Full Name (as per passport)", null, fullName);

    // Row 2: Designation / Role
    drawGridRow(
        "2.",
        "Designation / Role",
        "(e.g., speaker, author, student author, general participant)",
        designation
    );

    // Row 3: Organization / Institution Name
    drawGridRow("3.", "Organization / Institution Name", null, organization);

    // Row 4: Nationality / Country of Citizenship
    drawGridRow("4.", "Nationality / Country of Citizenship", null, nationality);

    // Row 5: Passport Details (Section Header)
    drawGridRow("5.", "Passport Details", null, "", true);

    // Sub-rows under Passport Details
    drawGridRow("", "Passport number", null, passportNumber);
    drawGridRow("", "Date of issue", null, dateOfIssue);
    drawGridRow("", "Date of expiry", null, dateOfExpiry);
    drawGridRow("", "Place of issue", null, placeOfIssue);
    drawGridRow("", "Country of birth", null, countryOfBirth);

    // Row 6: Contact Information (Section Header)
    drawGridRow("6.", "Contact Information", null, "", true);

    // Sub-rows under Contact Information
    drawGridRow("", "Email ID (Official/Personal)", null, emailId);
    drawGridRow("", "Mobile number", null, mobileNumber);

    // Row 7: Event Details / Affiliation
    drawGridRow(
        "7.",
        "Event Details / Affiliation",
        null,
        eventDetails
    );

    // -------------------------------------------------------------
    // 3. SIGNATURE SECTION (Below Table, matching specimen)
    // -------------------------------------------------------------
    currentY += 10;

    // Left side: Reference & Timestamp
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(`Clearance Ref: IVEC27-PC-${refId}`, startX, currentY + 5);
    doc.text(`Submitted On: ${submissionDate}`, startX, currentY + 9.5);
    doc.text("Portal: https://ivec2027.org.in/", startX, currentY + 14);

    // Right side: System-Generated Signature
    const rightAlignX = 196;
    doc.setFont("times", "bolditalic");
    doc.setFontSize(13);
    doc.setTextColor(11, 41, 84); // Navy
    doc.text(fullName, rightAlignX, currentY + 5, { align: "right" });

    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text("Digitally Signed via IVEC 2027 Portal", rightAlignX, currentY + 10, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("(Signature)", rightAlignX, currentY + 19, { align: "right" });

    // -------------------------------------------------------------
    // 4. FOOTER NOTE (Pinned at bottom of page)
    // -------------------------------------------------------------
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.3);
    doc.line(14, 276, 196, 276);

    doc.setFont("helvetica", "italic");
    doc.setFontSize(7.5);
    doc.setTextColor(70, 70, 70);
    doc.text(
        "* Note: This clearance document is electronically generated by the IVEC-2027 Conference Portal for Political Clearance submission to the Ministry of External Affairs (MEA) and Ministry of Home Affairs (MHA), Government of India.",
        105,
        281,
        { align: "center" }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(
        "IVEC 2027 Conference Secretariat | DRDO-MTRDC, Bengaluru, India | Page 1 of 1",
        105,
        286,
        { align: "center" }
    );

    const safeName = fullName.replace(/[^a-zA-Z0-9_-]/g, "_");
    const filename = `IVEC_2027_Political_Clearance_${safeName}.pdf`;

    if (shouldDownload) {
        doc.save(filename);
    }

    return doc;
};
